/**
 * User Management V2 state.
 *
 * Covers:
 *  - business/site selector
 *  - global user search
 *  - site user list
 *  - current user detail + view mode (read-only / edit)
 *  - leaver wizard state (3 steps + device list)
 *  - create user wizard state (13 steps)
 *  - misc sub-modals (open/closed flags)
 *
 * Mirrors monolith globals: currentUserDataV2, leaverUserData,
 * leaverWizardStep, leaverDevices, userV2WizardState, creatingUserV2.
 */

import { create } from 'zustand';
import {
  fetchBusinessUnits,
  fetchSitesForBusiness,
  fetchUsersForSite,
  searchUsers,
  fetchUserDetails,
  fetchUserDetailsFast,
  fetchUserAzureDetails,
  fetchUserGroups
} from '@/api/endpoints/userManagement.js';

const LEAVER_INITIAL = {
  step: 1,
  userDn: null,
  user: null,
  leavingDate: '',
  ticketNumber: '',
  convertToSharedMailbox: false,
  enableArchiving: false,
  cancelUpcomingMeetings: false,
  meetingWindowDays: 365,
  forwardingAddress: '',
  devices: [],
  devicesToWipe: [],
  processing: false,
  result: null
};

const CREATE_USER_INITIAL = {
  step: 1,
  business: '',
  site: '',
  copyFromUser: null,
  firstName: '',
  lastName: '',
  username: '',
  upn: '',
  emailPrefix: '',
  emailDomain: '',
  proxyAddresses: [],
  title: '',
  department: '',
  company: '',
  manager: null,
  officePhone: '',
  mobilePhone: '',
  ticketNumber: '',
  licenseGroups: [],
  groupMemberships: [],
  driveMappings: [],
  generatedPassword: ''
};

const CREATE_GENERIC_INITIAL = {
  step: 1,
  business: '',
  businessDN: '',
  businessCode: '',
  site: '',
  siteOU: '',
  accountType: null,
  includeTeamsMeetingRoom: false,
  displayName: '',
  username: '',
  usernameManuallyEdited: false,
  ticketNumber: '',
  company: '',
  office: '',
  mobilePhone: '',
  streetAddress: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  addressManuallyEdited: false,
  addressPrefilled: false,
  email: '',
  emailDomain: '',
  upn: '',
  additionalGroups: [],
  password: '',
  enableNow: true,
  enableFromDate: '',
  enableFromTime: '06:00'
};

export const useUserMgmtStore = create((set, get) => ({
  /* --------- directory selectors --------- */
  businessUnits: [],
  businessUnitsLoaded: false,
  business: '',
  sites: [],
  sitesLoading: false,
  site: '',
  siteUsers: [],
  siteUsersLoading: false,
  siteUsersError: null,

  /* --------- global search --------- */
  searchQuery: '',
  searchResults: null,
  searchLoading: false,
  searchError: null,

  /* --------- current user detail --------- */
  currentUser: null,
  currentUserLoading: false,
  currentUserError: null,
  detailModalOpen: false,
  viewMode: 'read', // 'read' | 'edit'

  /* --------- sub-modals --------- */
  resetPasswordOpen: false,
  // Change History moved out of the Devices & Security tab into the Actions
  // menu (Andy, after the first undo round trip): the history covers every
  // change to the account, not just its devices.
  changeHistoryOpen: false,
  emailChangeOpen: false,
  proxyAddressOpen: false,
  // The address being edited, or null when adding a new one. Lives in the store
  // because the panel that opens the modal and the modal itself are in
  // different branches of the detail-view tree.
  proxyAddressEdit: null,   // { address, isPrimary } | null
  addGroupOpen: false,
  removeGroupTarget: null,
  archiveModalOpen: false,
  mailDelegationOpen: false,
  calendarPermsOpen: false,
  mailRulesOpen: false,
  mailForwardingOpen: false,
  oneDriveDelegationOpen: false,
  mfaSessionsOpen: false,
  moveUserOpen: false,
  addLicenseOpen: false,
  addHostPoolOpen: false,
  // { [subModalFlagName]: closeCount } — see setSubModalOpen.
  subModalRefresh: {},

  /* --------- leaver wizard --------- */
  leaverOpen: false,
  leaver: { ...LEAVER_INITIAL },

  /* --------- create user wizard --------- */
  createOpen: false,
  createGenericOpen: false,
  create: { ...CREATE_USER_INITIAL },
  creating: false,

  /* --------- create generic account wizard --------- */
  createGeneric: { ...CREATE_GENERIC_INITIAL },
  creatingGeneric: false,

  /* ============== actions ============== */

  async loadBusinessUnits() {
    if (get().businessUnitsLoaded) return;
    try {
      const data = await fetchBusinessUnits();
      const list = Array.isArray(data) ? data : data?.businesses ?? [];
      set({ businessUnits: list, businessUnitsLoaded: true });
    } catch {
      set({ businessUnitsLoaded: true });
    }
  },

  /**
   * Business filter changed. Picking a business on its own lists everyone in
   * it (no search needed); a typed search is re-run scoped to it.
   */
  async loadSitesForBusiness(business) {
    set({ business, site: '', sites: [], sitesLoading: Boolean(business), siteUsers: [], siteUsersError: null, searchError: null });
    get()._reloadScope();
    if (!business) return;
    try {
      const data = await fetchSitesForBusiness(business);
      if (get().business !== business) return; // changed again meanwhile
      const list = Array.isArray(data) ? data : data?.sites ?? [];
      set({ sites: list, sitesLoading: false });
    } catch {
      if (get().business === business) set({ sites: [], sitesLoading: false });
    }
  },

  /** Site filter changed ('' = all sites in the business). */
  async loadUsersForSite(site) {
    set({ site });
    get()._reloadScope();
  },

  _scopeRequestId: 0,

  /** Re-fetch the scope list and, if a search is typed, re-run it in scope. */
  async _reloadScope() {
    if (get().searchQuery.trim().length >= 2) get().runSearch({ silent: true });
    const { business, site } = get();
    const requestId = get()._scopeRequestId + 1;
    set({ _scopeRequestId: requestId });
    if (!business) {
      set({ siteUsers: [], siteUsersLoading: false, siteUsersError: null });
      return;
    }
    set({ siteUsersLoading: true, siteUsers: [], siteUsersError: null });
    try {
      const data = await fetchUsersForSite(business, site);
      if (get()._scopeRequestId !== requestId) return; // stale
      const list = Array.isArray(data) ? data : data?.users ?? [];
      set({ siteUsers: list, siteUsersLoading: false });
    } catch (err) {
      if (get()._scopeRequestId !== requestId) return;
      set({
        siteUsers: [],
        siteUsersLoading: false,
        siteUsersError: err instanceof Error ? err.message : 'Failed to list users'
      });
    }
  },

  setSearchQuery(q) {
    set({ searchQuery: q });
  },

  _searchRequestId: 0,

  /** @param {{ silent?: boolean }} [opts] silent: true suppresses the
   * "enter at least 2 characters" error — used by the debounced
   * as-you-type caller so it doesn't flash an error while typing. */
  async runSearch(opts) {
    const silent = !!opts?.silent;
    const q = get().searchQuery.trim();
    const { business, site } = get();
    if (q.length < 2) {
      // With a business chosen the filter alone is a valid search — the
      // scope list is what is shown, so there's nothing to complain about.
      if (business) {
        set({ searchResults: null, searchError: null });
        if (!silent) get()._reloadScope();
        return;
      }
      if (!silent) set({ searchResults: [], searchError: 'Type at least 2 characters, or pick a business.' });
      return;
    }
    const requestId = ++get()._searchRequestId;
    set({ _searchRequestId: requestId, searchLoading: true, searchError: null });
    try {
      const data = await searchUsers(q, { business, site });
      if (get()._searchRequestId !== requestId) return; // stale response, ignore
      const list = Array.isArray(data) ? data : data?.users ?? [];
      set({ searchResults: list, searchLoading: false });
    } catch (err) {
      if (get()._searchRequestId !== requestId) return;
      set({
        searchResults: [],
        searchLoading: false,
        searchError: err instanceof Error ? err.message : 'Search failed'
      });
    }
  },

  async openUserDetail(dn) {
    // Open in edit mode — matches the monolith, where the detail view's
    // inline-editable template is the primary view.
    set({
      currentUserLoading: true,
      currentUserError: null,
      detailModalOpen: true,
      viewMode: 'edit'
    });
    try {
      // Fast path: AD record only (renders in ~1s), then Azure group
      // enrichment streams in behind it. Falls back to the combined
      // endpoint if the fast one is unavailable.
      let user;
      try {
        const data = await fetchUserDetailsFast(dn);
        user = data?.user ?? data;
      } catch {
        const data = await fetchUserDetails(dn);
        user = data?.user ?? data;
      }
      set({ currentUser: user, currentUserLoading: false });

      if (user?.azureEnrichmentPending && user.UserPrincipalName) {
        // Enrich in parallel: Azure user id + the merged/tagged group list
        // (AD memberOf + direct Graph memberOf, each tagged ad/azure/synced
        // and flagged dynamic). Group failure must not block the id enrichment.
        Promise.allSettled([
          fetchUserAzureDetails(user.UserPrincipalName),
          fetchUserGroups(user.UserPrincipalName)
        ])
          .then(([azureRes, groupsRes]) => {
            const current = get().currentUser;
            // Only apply if the same user is still open.
            if (current?.DistinguishedName !== user.DistinguishedName) return;
            const azure = azureRes.status === 'fulfilled' ? azureRes.value : null;
            const mergedGroups =
              groupsRes.status === 'fulfilled' && Array.isArray(groupsRes.value?.groups)
                ? groupsRes.value.groups
                : null;
            set({
              currentUser: {
                ...current,
                id: azure?.id ?? current.id ?? null,
                AzureGroups: azure?.AzureGroups ?? [],
                mergedGroups: mergedGroups ?? current.mergedGroups ?? null,
                azureEnrichmentPending: false
              }
            });
          })
          .catch(() => {
            const current = get().currentUser;
            if (current?.DistinguishedName === user.DistinguishedName) {
              set({ currentUser: { ...current, azureEnrichmentPending: false } });
            }
          });
      }
    } catch (err) {
      set({
        currentUserLoading: false,
        currentUserError: err instanceof Error ? err.message : 'Failed to load user'
      });
    }
  },

  setCurrentUser(user) {
    set({ currentUser: user });
  },

  setViewMode(mode) {
    set({ viewMode: mode === 'edit' ? 'edit' : 'read' });
  },

  closeUserDetail() {
    set({ detailModalOpen: false, currentUser: null, viewMode: 'read' });
  },

  async refreshCurrentUser() {
    const dn = get().currentUser?.DistinguishedName;
    if (!dn) return;
    try {
      const data = await fetchUserDetails(dn);
      const user = data?.user ?? data;
      // Preserve the enriched group list: /ad/user-details is an AD-only read,
      // so it carries no mergedGroups and flips azureEnrichmentPending back to
      // true. Replacing the record wholesale used to drop the tagged AD+Graph
      // list, and because the enrichment only runs in loadUser it was never
      // rebuilt — the panel silently fell back to AD DNs after every write.
      const previous = get().currentUser;
      set({
        currentUser: {
          ...user,
          mergedGroups: user?.mergedGroups ?? previous?.mergedGroups ?? null,
          AzureGroups: user?.AzureGroups ?? previous?.AzureGroups ?? [],
          id: user?.id ?? previous?.id ?? null,
          azureEnrichmentPending: false
        }
      });
    } catch {
      /* ignore */
    }
  },

  /**
   * Apply the result of a proxy-address write to the open record.
   *
   * The record is re-read first so every other field stays current, then the
   * address list is overwritten with the collection the WRITE itself reported.
   * That copy is authoritative: the backend read-modify-wrote the directory and
   * returned the resulting state, whereas the re-read can land on a domain
   * controller that has not replicated the change yet — which would put the old
   * list straight back on screen and look exactly like the write silently
   * failing, the very complaint this panel started with.
   *
   * A response with no address list (an older API) just falls through to the
   * re-read rather than blanking what is on screen.
   */
  async applyProxyAddressWrite(result) {
    await get().refreshCurrentUser();
    const written = Array.isArray(result?.proxyAddresses)
      ? result.proxyAddresses
      : Array.isArray(result?.rawAddresses)
        ? result.rawAddresses
        : null;
    if (!written) return;
    const current = get().currentUser;
    if (!current) return;
    set({ currentUser: { ...current, ProxyAddresses: written } });
  },

  /**
   * Re-read this person's group memberships and re-apply them to the open
   * record. Call after any membership write.
   *
   * `refresh: true` bypasses the 2-minute server-side cache — without it the
   * stale-while-revalidate read returns the pre-change list and the group the
   * operator just removed stays on screen for the rest of the TTL.
   *
   * `removedKeys` are groups the caller has just removed: they are filtered out
   * of the returned list even if the directory read still includes them, because
   * an Exchange or Graph membership change is eventually consistent and can
   * legitimately still report the old membership for a few seconds. Without this
   * the row would visibly reappear after the refresh.
   */
  async refreshCurrentUserGroups({ removedKeys = [] } = {}) {
    const user = get().currentUser;
    const upn = user?.UserPrincipalName;
    if (!upn) return;
    const dropped = new Set(removedKeys.map((k) => String(k ?? '').toLowerCase()).filter(Boolean));
    try {
      const res = await fetchUserGroups(upn, { refresh: true });
      const groups = Array.isArray(res?.groups) ? res.groups : null;
      const current = get().currentUser;
      // Only apply if the same person is still open.
      if (!current || current.UserPrincipalName !== upn) return;
      const filtered = groups
        ? groups.filter((g) => {
            const keys = [g.azureId, g.dn, g.name, g.displayName]
              .map((v) => String(v ?? '').toLowerCase())
              .filter(Boolean);
            return !keys.some((k) => dropped.has(k));
          })
        : null;
      set({ currentUser: { ...current, mergedGroups: filtered ?? current.mergedGroups ?? null, azureEnrichmentPending: false } });
    } catch {
      // A failed refresh must not wipe the list — the write already succeeded.
      // Fall back to filtering what is on screen so the removal is still visible.
      const current = get().currentUser;
      if (!current || current.UserPrincipalName !== upn || !Array.isArray(current.mergedGroups)) return;
      if (dropped.size === 0) return;
      set({
        currentUser: {
          ...current,
          mergedGroups: current.mergedGroups.filter((g) => {
            const keys = [g.azureId, g.dn, g.name, g.displayName]
              .map((v) => String(v ?? '').toLowerCase())
              .filter(Boolean);
            return !keys.some((k) => dropped.has(k));
          })
        }
      });
    }
  },

  /* --------- sub-modal openers --------- */
  setSubModalOpen(name, value) {
    if (value) {
      set({ [name]: value });
      return;
    }
    // Closing a sub-modal bumps a per-modal counter, which the panel behind it
    // passes to DetailSection as `refreshKey`. The panels read live directory
    // state through their own loader and cache the first result, so without
    // this a change made in the modal (a new delegate, a forward, a calendar
    // grant) only appeared after closing and reopening the whole user record.
    const counters = get().subModalRefresh;
    set({
      [name]: value,
      subModalRefresh: { ...counters, [name]: (counters[name] ?? 0) + 1 }
    });
  },

  /** Opens the proxy-address modal. Pass an entry to edit it, nothing to add. */
  openProxyAddress(edit = null) {
    set({ proxyAddressOpen: true, proxyAddressEdit: edit });
  },
  closeProxyAddress() {
    set({ proxyAddressOpen: false, proxyAddressEdit: null });
  },

  /* --------- leaver --------- */
  openLeaver(user) {
    set({
      leaverOpen: true,
      leaver: {
        ...LEAVER_INITIAL,
        userDn: user?.DistinguishedName ?? null,
        user
      }
    });
  },
  closeLeaver() {
    set({ leaverOpen: false, leaver: { ...LEAVER_INITIAL } });
  },
  patchLeaver(patch) {
    set((s) => ({ leaver: { ...s.leaver, ...patch } }));
  },
  setLeaverStep(step) {
    set((s) => ({ leaver: { ...s.leaver, step } }));
  },

  /* --------- create user --------- */
  openCreate() {
    set({ createOpen: true, create: { ...CREATE_USER_INITIAL } });
  },
  closeCreate() {
    set({ createOpen: false, create: { ...CREATE_USER_INITIAL } });
  },
  patchCreate(patch) {
    set((s) => ({ create: { ...s.create, ...patch } }));
  },
  setCreateStep(step) {
    set((s) => ({ create: { ...s.create, step } }));
  },
  setCreating(creating) {
    set({ creating });
  },

  openCreateGeneric() {
    set({ createGenericOpen: true, createGeneric: { ...CREATE_GENERIC_INITIAL } });
  },
  closeCreateGeneric() {
    set({ createGenericOpen: false, createGeneric: { ...CREATE_GENERIC_INITIAL }, creatingGeneric: false });
  },
  patchCreateGeneric(patch) {
    set((s) => ({ createGeneric: { ...s.createGeneric, ...patch } }));
  },
  setCreateGenericStep(step) {
    set((s) => ({ createGeneric: { ...s.createGeneric, step } }));
  },
  setCreatingGeneric(creatingGeneric) {
    set({ creatingGeneric });
  }
}));

/* --------- selectors --------- */

export function selectIsLocked(user) {
  if (!user) return false;
  return !!(user.lockoutTime > 0 || user.LockedOut);
}

/**
 * T-account tier ("T1".."T4") detected from the account's OU — same rule the
 * Users dashboard widget uses server-side (OUs "T1 Accounts", "T2 Accounts",
 * "T3 Accounts", "T4 User Accounts"). Works off the DN when present, falling
 * back to the derived location path on search-card shapes. Null for normal
 * user accounts.
 */
export function selectTAccountTier(user) {
  if (!user) return null;
  const haystack = `${user.DistinguishedName || user.distinguishedName || ''}|${user.location || user.Location || ''}`;
  const match = haystack.match(/\bT([1-3]) Accounts\b|\bT(4) User Accounts\b/i);
  if (!match) return null;
  return `T${match[1] || match[2]}`;
}

/**
 * Account-type catalog for the search filter + coloured result pills.
 *
 * Detection is heuristic (mirrors the naming conventions the Create Generic
 * wizard applies) and best-effort, same spirit as the T-account OU rule:
 *  - Service Account → samAccountName starts "svc_"
 *  - Standard generic types → samAccountName starts "<Type>." (e.g. "CCTV.")
 *    per suggestGenericUsername's "AccountType.BusinessCode.SiteName" format
 *  - T1–T4 → OU-based (selectTAccountTier)
 *
 * Driver accounts (username = vehicle registration) have no reliable signal,
 * so they are intentionally not detectable/filterable here.
 */
export const GENERIC_ACCOUNT_TYPE_META = [
  { id: 'ServiceAccount', label: 'Service Account', color: '#0d9488', icon: 'fa-server', match: (sam) => /^svc_/i.test(sam) },
  { id: 'CCTV', label: 'CCTV', color: '#dc2626', icon: 'fa-video', match: (sam) => /^cctv\./i.test(sam) },
  { id: 'Tacho', label: 'Tacho', color: '#ea580c', icon: 'fa-tachometer-alt', match: (sam) => /^tacho\./i.test(sam) },
  { id: 'Technician', label: 'Technician', color: '#0891b2', icon: 'fa-wrench', match: (sam) => /^technician\./i.test(sam) },
  { id: 'Boardroom', label: 'Boardroom', color: '#db2777', icon: 'fa-tv', match: (sam) => /^boardroom\./i.test(sam) },
  { id: 'Maha', label: 'Maha', color: '#c026d3', icon: 'fa-cogs', match: (sam) => /^maha\./i.test(sam) },
  { id: 'Workshop', label: 'Workshop', color: '#b45309', icon: 'fa-tools', match: (sam) => /^workshop\./i.test(sam) }
];

export const T_ACCOUNT_TYPE_META = [
  { id: 'T1', label: 'T1 Account', color: '#4f46e5', icon: 'fa-user-shield' },
  { id: 'T2', label: 'T2 Account', color: '#7c3aed', icon: 'fa-user-shield' },
  { id: 'T3', label: 'T3 Account', color: '#9333ea', icon: 'fa-user-shield' },
  { id: 'T4', label: 'T4 Account', color: '#c026d3', icon: 'fa-user-shield' }
];

/** Grouped options for the account-type filter dropdown. */
export const ACCOUNT_TYPE_FILTER_GROUPS = [
  { label: 'T Accounts', options: T_ACCOUNT_TYPE_META },
  { label: 'Generic Accounts', options: GENERIC_ACCOUNT_TYPE_META }
];

function samAccountNameOf(user) {
  const direct = user.samAccountName || user.SamAccountName;
  if (direct) return String(direct);
  const upn = user.UserPrincipalName || user.userPrincipalName || user.upn || '';
  return upn.includes('@') ? upn.split('@')[0] : String(upn);
}

/**
 * Resolves an account's type descriptor ({ id, label, color, icon }) for the
 * coloured search pill and the account-type filter. Generic types win over
 * T-tier when both somehow match. Null for ordinary user accounts.
 */
export function selectAccountType(user) {
  if (!user) return null;
  const sam = samAccountNameOf(user);
  const generic = GENERIC_ACCOUNT_TYPE_META.find((t) => t.match(sam));
  if (generic) return { id: generic.id, label: generic.label, color: generic.color, icon: generic.icon };
  const tier = selectTAccountTier(user);
  if (tier) {
    const meta = T_ACCOUNT_TYPE_META.find((t) => t.id === tier);
    return { id: tier, label: `${tier} Account`, color: meta?.color ?? '#8b5cf6', icon: 'fa-user-shield' };
  }
  return null;
}

export function selectAccountStatus(user) {
  if (!user) return { state: 'unknown', label: '—', color: 'var(--faint)' };
  if (user.Enabled === false) return { state: 'disabled', label: 'Disabled', color: '#dc2626', bg: '#fee2e2', icon: 'fa-times-circle' };
  if (selectIsLocked(user)) return { state: 'locked', label: 'Locked Out', color: '#d97706', bg: '#fef3c7', icon: 'fa-lock' };
  return { state: 'enabled', label: 'Enabled', color: '#15803d', bg: '#dcfce7', icon: 'fa-check-circle' };
}
